import { Component, Vue, PropSync, Watch, Emit } from 'vue-property-decorator';
import { TableClickEvent } from '@/models/tables/TableClickEvent';
import { TableMenuItem } from '@/models/general/TableMenuItem';
import { TableMenuActionEnums, TableMenuActionColorEnums } from '@/enums/TableMenuActionEnums';
import { LoggerService } from '@/utils';
import { TableMenuClickEvent } from '@/models/tables/TableMenuClickEvent';

@Component
export default class TableCellMenuComponent extends Vue {
    @PropSync('show', { type: Boolean }) public syncedShow!: boolean;
    @PropSync('event', { type: Object }) public syncedEvent!: TableClickEvent;
    @PropSync('delay', { type: Number }) public syncedDelay!: number;
    @PropSync('fixedRows', { type: Number }) public syncedFixedRows!: number;

    public menuItems: TableMenuItem[] = [];

    // tslint:disable-next-line: no-empty
    public mounted() {
        // this.menuItems.push(new TableRowMenuItem({ textId: 'fixCol', icon: '$vuetify.icons.fixRow', action: TableMenuActionEnums.FIX_COLUMN }));
        this.menuItems.push(new TableMenuItem({ textId: 'fixRow', icon: '$vuetify.icons.fixRow', action: TableMenuActionEnums.FIX_ROW, disabled: this.syncedFixedRows === 4 }));
        this.menuItems.push(new TableMenuItem({ textId: 'editValue', icon: 'mdi-pencil', action: TableMenuActionEnums.EDIT_VALUE }));
        this.menuItems.push(new TableMenuItem({ textId: 'colourField', icon: 'mdi-palette', action: TableMenuActionEnums.COLOUR_FIELD }));
        this.menuItems.push(new TableMenuItem({ textId: 'cleanAllMarks', icon: 'mdi-backspace', action: TableMenuActionEnums.CLEAN_ALL_MARKS }));
        this.menuItems.push(new TableMenuItem({ textId: 'copyForQuickPaste', icon: '$vuetify.icons.copyQuickPaste', action: TableMenuActionEnums.COPY_FOR_QUICK_PASTE }));
        this.menuItems.push(new TableMenuItem({ textId: 'previewDataset', icon: 'mdi-magnify', action: TableMenuActionEnums.PREVIEW_DATASET }));
        this.menuItems.push(new TableMenuItem({ textId: 'editLine', icon: '$vuetify.icons.editLine', action: TableMenuActionEnums.EDIT_LINE }));
        this.menuItems.push(new TableMenuItem({ textId: 'cloneLine', icon: 'mdi-file-multiple', action: TableMenuActionEnums.CLONE_LINE }));
        this.menuItems.push(new TableMenuItem({ textId: 'deleteLine', icon: 'mdi-delete', action: TableMenuActionEnums.DELETE_LINE }));
        // this.menuItems.push(new TableRowMenuItem({ textId: 'cloneTest', icon: 'mdi-format-indent-increase', action: TableRowMenuActionEnums.CLONE_TEST_SERIES }));
        this.menuItems.push(new TableMenuItem({ textId: 'report', icon: '$vuetify.icons.settingsReport', action: TableMenuActionEnums.SETTINGS_REPORT }));
        // this.menuItems.push(new TableMenuItem({ textId: 'exportTestCsv', icon: 'mdi-application-export', action: TableMenuActionEnums.EXPORT_TEST_SERIES_CSV }));
        this.menuItems.push(new TableMenuItem({ textId: 'exportTestExcel', icon: 'mdi-application-export', action: TableMenuActionEnums.EXPORT_TEST_SERIES_EXCEL }));
        this.updateOwnerActions(this.syncedEvent && this.syncedEvent.isOwner === true);
    }

    get visibleMenuItems(): TableMenuItem[] {
        return this.menuItems.filter((item) => item.hidden !== true);
    }

    public updated() {
        let item: TableMenuItem | undefined;
        if (this.syncedEvent.disableColors !== undefined) {
            item = this.menuItems.find((mi) => mi.action === TableMenuActionEnums.COLOUR_FIELD);
            if (item) {
                item.disabled = this.syncedEvent.disableColors;
            }
        }
        if (this.syncedEvent.disableEdit !== undefined) {
            item = this.menuItems.find((mi) => mi.action === TableMenuActionEnums.EDIT_VALUE);
            if (item) {
                item.disabled = this.syncedEvent.disableEdit;
            }
        }
    }

    @Watch('syncedEvent', { deep: true })
    public onSyncedEventChanged(newValue: TableClickEvent) {
        this.changeFixRow(newValue.isFixed);
        this.updateOwnerActions(newValue.isOwner === true);
    }

    private updateOwnerActions(isOwner: boolean) {
        this.menuItems.forEach((item) => {
            if (
                item.action === TableMenuActionEnums.EDIT_LINE ||
                item.action === TableMenuActionEnums.DELETE_LINE ||
                item.action === TableMenuActionEnums.EDIT_VALUE
            ) {
                item.hidden = !isOwner;
            }
        });
    }

    @Emit()
    public itemClicked(menuAction: TableMenuActionEnums, color?: TableMenuActionColorEnums): TableMenuClickEvent {
        return new TableMenuClickEvent({
            dataSetId: this.syncedEvent.dataSetId,
            dataSetCellId: this.syncedEvent.dataSetCellId,
            action: menuAction,
            color,
        });
    }

    public onItemClicked(index: number) {
        const item = this.visibleMenuItems[index];
        LoggerService.LogDebug(`Item clicked: ${index}`, item);
        if (item.action !== TableMenuActionEnums.COLOUR_FIELD) {
            this.syncedShow = false;
            this.itemClicked(item.action);
        }
    }

    public onColorButtonClicked(index: number) {
        let color: TableMenuActionColorEnums = TableMenuActionColorEnums.UNMARK;
        switch (index) {
            case 0:
                color = TableMenuActionColorEnums.UNMARK;
                break;
            case 1:
                color = TableMenuActionColorEnums.ORANGE;
                break;
            case 2:
                color = TableMenuActionColorEnums.GREY;
                break;
            case 3:
                color = TableMenuActionColorEnums.BLUE;
                break;

            default:
                break;
        }

        this.syncedShow = false;
        this.itemClicked(TableMenuActionEnums.COLOUR_FIELD, color);
    }

    private changeFixRow(showUnfix: boolean) {
        if (showUnfix === true) {
            const menuItem = this.menuItems.find((item) => item.action === TableMenuActionEnums.FIX_ROW);
            if (menuItem !== undefined) {
                menuItem.action = TableMenuActionEnums.UNFIX_ROW;
                menuItem.textId = 'unfixRow';
                menuItem.disabled = false;
            }
        } else {
            const menuItem = this.menuItems.find((item) => item.action === TableMenuActionEnums.UNFIX_ROW);
            if (menuItem !== undefined) {
                menuItem.action = TableMenuActionEnums.FIX_ROW;
                menuItem.textId = 'fixRow';
                menuItem.disabled = this.syncedFixedRows === 5;
            } else {
                const menuItem2 = this.menuItems.find((item) => item.action === TableMenuActionEnums.FIX_ROW);
                if (menuItem2 !== undefined) {
                    menuItem2.disabled = this.syncedFixedRows === 5;
                }
            }
        }
    }
}
